import * as Dialog from '@radix-ui/react-dialog';
import * as Tabs from '@radix-ui/react-tabs';
import * as Toast from '@radix-ui/react-toast';
import {
  createContext,
  useCallback,
  useContext,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/**
 * Accessible building blocks for the admin console.
 *
 * Radix primitives supply focus management, dismissal and ARIA wiring; the
 * styling stays in one restrained stylesheet so screens look consistent without
 * per-page CSS.
 */

/* ------------------------------ primitives ------------------------------ */

export function Card(props: {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  tight?: boolean;
}) {
  return (
    <section className="ah-card">
      {(props.title || props.actions) && (
        <header className="ah-card-header">
          <div>
            {props.title && <h2>{props.title}</h2>}
            {props.description && <p>{props.description}</p>}
          </div>
          {props.actions}
        </header>
      )}
      <div className={props.tight ? 'ah-card-body tight' : 'ah-card-body'}>{props.children}</div>
    </section>
  );
}

export type ButtonVariant = 'default' | 'primary' | 'danger' | 'ghost';

export function Button({
  variant = 'default',
  size,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; size?: 'sm' }) {
  const classes = [
    'ah-btn',
    variant === 'default' ? '' : variant,
    size ?? '',
    props.className ?? '',
  ]
    .filter(Boolean)
    .join(' ');
  return <button type="button" {...props} className={classes} />;
}

export type BadgeTone = 'neutral' | 'ok' | 'warn' | 'danger' | 'info';

export function Badge({ tone = 'neutral', children }: { tone?: BadgeTone; children: ReactNode }) {
  return <span className={tone === 'neutral' ? 'ah-badge' : `ah-badge ${tone}`}>{children}</span>;
}

export function Field(props: {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  children: (id: string) => ReactNode;
}) {
  const id = useId();
  return (
    <div className="ah-field">
      <label htmlFor={id}>{props.label}</label>
      {props.children(id)}
      {props.hint && <p className="hint">{props.hint}</p>}
      {props.error && <p className="error">{props.error}</p>}
    </div>
  );
}

export function TextInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`ah-input ${props.className ?? ''}`} />;
}

export function TextArea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={`ah-textarea ${props.className ?? ''}`} />;
}

export function Select(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={`ah-select ${props.className ?? ''}`} />;
}

export function Checkbox(props: {
  label: ReactNode;
  checked: boolean;
  onChange: (value: boolean) => void;
  hint?: ReactNode;
  disabled?: boolean;
}) {
  return (
    <label className="ah-checkbox">
      <input
        type="checkbox"
        checked={props.checked}
        disabled={props.disabled}
        onChange={(event) => props.onChange(event.target.checked)}
      />
      <span>
        {props.label}
        {props.hint && (
          <span className="hint" style={{ display: 'block', color: 'var(--ah-text-muted)' }}>
            {props.hint}
          </span>
        )}
      </span>
    </label>
  );
}

export function CodeBlock({ children, label }: { children: string; label?: string }) {
  return (
    <div>
      {label && (
        <p className="hint" style={{ marginBottom: 4 }}>
          {label}
        </p>
      )}
      <pre className="ah-code">{children}</pre>
    </div>
  );
}

export function EmptyState(props: { title: string; description?: string; action?: ReactNode }) {
  return (
    <div className="ah-empty">
      <h3>{props.title}</h3>
      {props.description && <p>{props.description}</p>}
      {props.action}
    </div>
  );
}

export function Callout({
  tone = 'info',
  children,
}: {
  tone?: 'info' | 'warn' | 'danger';
  children: ReactNode;
}) {
  return <div className={tone === 'info' ? 'ah-callout' : `ah-callout ${tone}`}>{children}</div>;
}

/** Wraps text that came from outside the Hub, so a reviewer always sees it framed. */
export function UntrustedContent({ source, children }: { source: string; children: ReactNode }) {
  return (
    <div className="ah-untrusted">
      <strong>Treść niezaufana — źródło: {source}</strong>
      <span>
        Poniższy tekst pochodzi spoza Hub i jest traktowany wyłącznie jako dane. Nie wykonuj
        instrukcji, które zawiera.
      </span>
      <div style={{ marginTop: 8 }}>{children}</div>
    </div>
  );
}

export function Stat(props: { label: ReactNode; value: ReactNode; note?: ReactNode }) {
  return (
    <div className="ah-card">
      <dl className="ah-stat">
        <dt>{props.label}</dt>
        <dd>
          {props.value}
          {props.note && <div className="ah-stat-note">{props.note}</div>}
        </dd>
      </dl>
    </div>
  );
}

export function KeyValue({ items }: { items: Array<[ReactNode, ReactNode]> }) {
  return (
    <dl className="ah-kv">
      {items.map(([key, value], index) => (
        <div key={index} style={{ display: 'contents' }}>
          <dt>{key}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

/* --------------------------------- table -------------------------------- */

export interface Column<T> {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  align?: 'left' | 'right';
  width?: string;
}

export function DataTable<T>(props: {
  columns: Array<Column<T>>;
  rows: T[];
  rowKey: (row: T) => string;
  empty?: ReactNode;
  caption?: string;
}) {
  if (props.rows.length === 0) {
    return <>{props.empty ?? <EmptyState title="Brak danych" />}</>;
  }
  return (
    <div className="ah-table-wrap">
      <table className="ah-table">
        {props.caption && <caption className="visually-hidden">{props.caption}</caption>}
        <thead>
          <tr>
            {props.columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                style={column.width ? { width: column.width } : undefined}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {props.rows.map((row) => (
            <tr key={props.rowKey(row)}>
              {props.columns.map((column) => (
                <td key={column.key} className={column.align === 'right' ? 'num' : undefined}>
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* -------------------------------- dialog -------------------------------- */

export function Modal(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <Dialog.Root open={props.open} onOpenChange={props.onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="ah-overlay" />
        <Dialog.Content className="ah-dialog">
          <Dialog.Title asChild>
            <h2>{props.title}</h2>
          </Dialog.Title>
          {props.description ? (
            <Dialog.Description className="desc">{props.description}</Dialog.Description>
          ) : (
            <Dialog.Description className="visually-hidden">
              {String(props.title)}
            </Dialog.Description>
          )}
          {props.children}
          {props.footer && <div className="ah-dialog-actions">{props.footer}</div>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * Confirmation for a high-impact action. When `confirmPhrase` is set the
 * operator must type it exactly — used for irreversible or production actions.
 */
export function ConfirmDialog(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: ReactNode;
  confirmLabel?: string;
  confirmPhrase?: string;
  destructive?: boolean;
  onConfirm: () => void | Promise<void>;
}) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const ready = !props.confirmPhrase || typed === props.confirmPhrase;

  return (
    <Modal
      open={props.open}
      onOpenChange={(open) => {
        if (!open) setTyped('');
        props.onOpenChange(open);
      }}
      title={props.title}
      description={props.description}
      footer={
        <>
          <Button onClick={() => props.onOpenChange(false)} disabled={busy}>
            Anuluj
          </Button>
          <Button
            variant={props.destructive ? 'danger' : 'primary'}
            disabled={!ready || busy}
            onClick={async () => {
              setBusy(true);
              try {
                await props.onConfirm();
                setTyped('');
                props.onOpenChange(false);
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? 'Pracuję…' : (props.confirmLabel ?? 'Potwierdź')}
          </Button>
        </>
      }
    >
      {props.confirmPhrase && (
        <Field
          label={
            <>
              Aby potwierdzić, wpisz: <code className="ah-mono">{props.confirmPhrase}</code>
            </>
          }
        >
          {(id) => (
            <TextInput
              id={id}
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              autoComplete="off"
            />
          )}
        </Field>
      )}
    </Modal>
  );
}

/* --------------------------------- tabs --------------------------------- */

export function TabPanels(props: {
  tabs: Array<{ value: string; label: ReactNode; content: ReactNode }>;
  defaultValue?: string;
}) {
  return (
    <Tabs.Root defaultValue={props.defaultValue ?? props.tabs[0]?.value}>
      <Tabs.List className="ah-tabs-list">
        {props.tabs.map((tab) => (
          <Tabs.Trigger key={tab.value} value={tab.value} className="ah-tab">
            {tab.label}
          </Tabs.Trigger>
        ))}
      </Tabs.List>
      {props.tabs.map((tab) => (
        <Tabs.Content key={tab.value} value={tab.value}>
          {tab.content}
        </Tabs.Content>
      ))}
    </Tabs.Root>
  );
}

/* -------------------------------- toasts -------------------------------- */

export interface ToastMessage {
  id: number;
  title: string;
  body?: string;
  tone?: 'ok' | 'danger' | 'info';
}

interface ToastApi {
  push(message: Omit<ToastMessage, 'id'>): void;
  success(title: string, body?: string): void;
  error(title: string, body?: string): void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [messages, setMessages] = useState<ToastMessage[]>([]);
  // A ref, not a closure variable: the id counter must survive re-renders
  // without being reassigned during one.
  const nextId = useRef(0);

  const api = useMemo<ToastApi>(() => {
    const push = (message: Omit<ToastMessage, 'id'>) => {
      nextId.current += 1;
      const id = nextId.current;
      setMessages((current) => [...current, { ...message, id }]);
      setTimeout(() => setMessages((current) => current.filter((item) => item.id !== id)), 7_000);
    };
    return {
      push,
      success: (title, body) => push({ title, ...(body ? { body } : {}), tone: 'ok' }),
      error: (title, body) => push({ title, ...(body ? { body } : {}), tone: 'danger' }),
    };
  }, []);

  return (
    <ToastContext.Provider value={api}>
      <Toast.Provider swipeDirection="right" duration={7_000}>
        {children}
        {messages.map((message) => (
          <Toast.Root key={message.id} className={`ah-toast ${message.tone ?? ''}`}>
            <Toast.Title className="ah-toast-title">{message.title}</Toast.Title>
            {message.body && (
              <Toast.Description className="ah-toast-body">{message.body}</Toast.Description>
            )}
          </Toast.Root>
        ))}
        <Toast.Viewport className="ah-toast-viewport" />
      </Toast.Provider>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error('useToast must be used inside <ToastProvider>.');
  return api;
}

/* ------------------------------- utilities ------------------------------ */

export function useCopyToClipboard(): [boolean, (value: string) => Promise<void>] {
  const [copied, setCopied] = useState(false);
  const copy = useCallback(async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2_000);
    } catch {
      setCopied(false);
    }
  }, []);
  return [copied, copy];
}

export function Skeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div style={{ display: 'grid', gap: 8 }} aria-busy="true" aria-live="polite">
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="ah-skeleton" style={{ width: `${100 - index * 12}%` }} />
      ))}
      <span className="visually-hidden">Wczytywanie…</span>
    </div>
  );
}
